import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  type OnInit,
  signal,
} from "@angular/core";
import { NavigationEnd, Router, RouterOutlet } from "@angular/router";

import { TranslateService } from "@ngx-translate/core";
import { MessageService } from "primeng/api";
import { filter } from "rxjs";

import { NavigationService } from "../core/services/navigation.service";
import { OrderView } from "../features/wms/shared/models";
import { WarehouseStore } from "../features/wms/warehouse/warehouse.store";
import { VersioningService } from "../shared/services/versioning.service";
import { ViewportService } from "../shared/services/viewport.service";
import { DrawerComponent } from "./drawer/drawer.component";
import { HeaderComponent } from "./header/header.component";

/**
 * Main layout component that manages the application's responsive layout structure.
 * Handles drawer visibility based on screen size, provides navigation toggle functionality,
 * and manages error notifications related to order preparation.
 */
@Component({
  selector: "app-layout",
  imports: [RouterOutlet, HeaderComponent, DrawerComponent],
  templateUrl: "./layout.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LayoutComponent implements OnInit {
  viewportService = inject(ViewportService);
  private versioningService = inject(VersioningService);
  private readonly messageService = inject(MessageService);
  private readonly translate = inject(TranslateService);
  private readonly router = inject(Router);

  private readonly warehouseStore = inject(WarehouseStore);

  isDrawerOpen = signal(this.viewportService.isDesktop());
  readonly toggleDrawer = () => {
    this.isDrawerOpen.set(!this.isDrawerOpen());
  };

  /**
   * Initializes the layout component, setting up responsive drawer behavior and error handling.
   */
  constructor() {
    inject(NavigationService); // Needed to initialize NavigationService

    /**
     * Synchronizes the drawer open state with the viewport size.
     * Automatically opens the drawer when the viewport matches desktop dimensions.
     */
    effect(() => {
      this.isDrawerOpen.set(this.viewportService.isDesktop()); // Drawer should be open on desktop devices
    });

    /**
     * Monitors the `TO_PREPARE` view state for errors.
     * Triggers a UI toast notification and resets the error state immediately after display.
     */
    effect(() => {
      const error = this.warehouseStore.views().TO_PREPARE.error;
      if (!error) return;

      this.messageService.add({
        severity: "error",
        summary: this.translate.instant("common.error"),
        detail: this.translate.instant(error),
      });

      this.warehouseStore.clearError(OrderView.TO_PREPARE);
    });

    /**
     * Watches for critical picking errors in the global store state.
     * Displays a sticky error message to the user and clears the critical error flag.
     */
    effect(() => {
      const error = this.warehouseStore.criticalPickingError();
      if (!error) return;

      this.messageService.add({
        severity: "error",
        summary: this.translate.instant("common.error"),
        detail: this.translate.instant(error),
        sticky: true,
      });

      this.warehouseStore.clearCriticalPickingError();
    });

    // Start or stop orders auto-refresh based on current route
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => {
        const section = this.getCurrentSection();

        if (section?.startsWith("wms/warehouse")) {
          this.warehouseStore.startAutoRefresh();
        } else {
          this.warehouseStore.stopAutoRefresh();
        }
      });
  }

  /**
   * Performs application cleanup by removing old version content.
   */
  ngOnInit(): void {
    // Clean old versions of content
    this.versioningService.cleanOld();
  }

  /**
   * Retrieves the current section from the router's activated route.
   *
   * @returns {string | undefined} The current section name, if defined in route data.
   */
  private getCurrentSection(): string | undefined {
    let route = this.router.routerState.root;

    while (route.firstChild) {
      route = route.firstChild;
    }

    // biome-ignore lint/complexity/useLiteralKeys: required access by TS on an index signature
    return route.snapshot.data["section"];
  }
}
